import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Search, Edit2, Trash2, Eye, EyeOff, RefreshCw,
  Package, Filter, Save, X, Star, Upload, Tag, ChevronDown, Check
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  nom: '', description: '', description_longue: '', type: 'prestation',
  categorie_id: '', univers: [], images: [''], duree: '',
  tarifs: { particulier: 0, entreprise: 0, association: 0, ce: 0, groupe: 0 },
  disponible: true, featured: false, order_weight: 50,
  stock: '', quantite_disponible: 1, produits_associes: [], tags: '', seo_title: '', seo_description: ''
};

const TYPE_CONFIG = {
  prestation:  { label: 'Prestation',  color: '#9385EC', bg: 'rgba(147,133,236,0.12)' },
  location:    { label: 'Location',    color: 'var(--admin-teal)', bg: 'rgba(93,188,191,0.1)' },
  vente:       { label: 'Vente',       color: 'var(--admin-green)', bg: 'rgba(31,157,122,0.1)' },
  precommande: { label: 'Précommande', color: 'var(--admin-amber)', bg: 'rgba(197,139,25,0.1)' },
  fabrication: { label: 'Fabrication', color: 'var(--admin-purple)', bg: 'rgba(139,92,246,0.1)' },
};

function ProductForm({ product, categories, onSave, onCancel }) {
  const [form, setForm] = useState(product ? {
    ...product,
    tags: Array.isArray(product.tags) ? product.tags.join(', ') : product.tags || '',
    univers: product.univers || [],
    images: product.images?.length ? product.images : [''],
  } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [venteProducts, setVenteProducts] = useState([]);

  useEffect(() => {
    fetch(`${BACKEND}/api/products?product_type=vente&limit=200`)
      .then(r => r.json())
      .then(d => setVenteProducts(d.products || []))
      .catch(() => {});
  }, []);

  function toggleAssocie(id) {
    setForm(prev => {
      const cur = prev.produits_associes || [];
      return { ...prev, produits_associes: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
    });
  }

  function setF(key, val) { setForm(prev => ({ ...prev, [key]: val })); }
  function setTarif(profil, val) {
    setForm(prev => ({ ...prev, tarifs: { ...prev.tarifs, [profil]: parseFloat(val) || 0 } }));
  }
  function autoTarifs(base) {
    const b = parseFloat(base) || 0;
    setForm(prev => ({ ...prev, tarifs: {
      particulier: b,
      entreprise: parseFloat((b*0.85).toFixed(2)),
      association: parseFloat((b*0.85).toFixed(2)),
      ce: parseFloat((b*0.90).toFixed(2)),
      groupe: parseFloat((b*0.80).toFixed(2)),
    }}));
  }

  async function submit() {
    if (!form.nom.trim()) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
      const body = {
        ...form,
        tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags,
        images: form.images.filter(Boolean),
        stock: form.stock !== '' ? parseInt(form.stock) : null,
      };
      const method = product ? 'PUT' : 'POST';
      const url = product ? `${BACKEND}/api/products/${product.id}` : `${BACKEND}/api/products`;
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      toast.success(product ? 'Produit mis à jour !' : 'Produit créé !');
      onSave();
    } catch { toast.error('Erreur sauvegarde'); }
    setSaving(false);
  }

  const PROFILS = ['particulier', 'entreprise', 'association', 'ce', 'groupe'];
  const PROFIL_LABELS = { particulier: 'Particulier', entreprise: 'Entreprise', association: 'Association', ce: 'CE', groupe: 'Partenaire' };
  const PROFIL_COLORS = { particulier: '#9CA3AF', entreprise: '#5DBCBF', association: '#7DB1FF', ce: '#C58B19', groupe: '#D4AF37' };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
      style={{ backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.7)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="w-full max-w-2xl rounded-2xl my-4"
        style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)' }}>
        <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid var(--admin-border-soft)' }}>
          <h2 className="font-bold text-lg" style={{ color: 'var(--admin-text)' }}>{product ? 'Modifier le produit' : 'Nouveau produit'}</h2>
          <button onClick={onCancel} style={{ color: 'var(--admin-muted)' }}><X size={20}/></button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto" style={{ maxHeight: '75vh' }}>
          {/* Infos de base */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Nom du produit *</label>
              <Input value={form.nom} onChange={e => setF('nom', e.target.value)} placeholder="Ex: Jeu gonflable Marin"
                style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Type</label>
              <select value={form.type} onChange={e => setF('type', e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)' }}>
                {Object.entries(TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Catégorie</label>
              <select value={form.categorie_id || ''} onChange={e => setF('categorie_id', e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)' }}>
                <option value="">-- Aucune --</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-3">
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Description courte</label>
              <textarea value={form.description} onChange={e => setF('description', e.target.value)}
                rows={2} placeholder="Description affichée en liste..."
                className="w-full text-sm px-3 py-2 rounded-lg resize-y"
                style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Description longue</label>
              <textarea value={form.description_longue} onChange={e => setF('description_longue', e.target.value)}
                rows={3} placeholder="Description détaillée page produit..."
                className="w-full text-sm px-3 py-2 rounded-lg resize-y"
                style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
            </div>
          </div>

          {/* Durée / temps (prestations) */}
          {(form.type === 'prestation') && (
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Durée / temps de prestation</label>
              <Input value={form.duree || ''} onChange={e => setF('duree', e.target.value)}
                placeholder="Ex: 2h, demi-journée, sur place 4h..."
                style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
            </div>
          )}

          {/* Image */}
          <div>
            <label className="text-xs mb-2 block" style={{ color: 'var(--admin-muted)' }}>Images (URL)</label>
            {(form.images || ['']).map((img, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input value={img} onChange={e => {
                  const imgs = [...(form.images || [''])];
                  imgs[i] = e.target.value;
                  setF('images', imgs);
                }} placeholder="https://... ou URL Unsplash"
                  style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
                {img && <img src={img} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" onError={e => e.target.style.display='none'} />}
                <button onClick={() => setF('images', form.images.filter((_,j)=>j!==i))} style={{ color: 'var(--admin-red)' }}><X size={14}/></button>
              </div>
            ))}
            <button onClick={() => setF('images', [...(form.images||['']), ''])}
              className="text-xs flex items-center gap-1" style={{ color: 'var(--admin-teal)' }}>
              <Plus size={12}/> Ajouter une image
            </button>
          </div>

          {/* Tarifs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs" style={{ color: 'var(--admin-muted)' }}>Tarifs par profil (€/jour)</label>
              {form.tarifs.particulier > 0 && (
                <button onClick={() => autoTarifs(form.tarifs.particulier)}
                  className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(212,175,55,0.15)', color: 'var(--admin-gold)' }}>
                  Auto-calculer les remises
                </button>
              )}
            </div>
            <div className="grid grid-cols-5 gap-2">
              {PROFILS.map(p => (
                <div key={p}>
                  <label className="text-xs mb-1 block" style={{ color: PROFIL_COLORS[p] }}>{PROFIL_LABELS[p]}</label>
                  <input type="number" min="0" step="0.5" value={form.tarifs[p] || ''}
                    onChange={e => setTarif(p, e.target.value)}
                    className="w-full text-sm text-center px-2 py-1.5 rounded-lg"
                    style={{ background: 'var(--admin-surface)', border: `1px solid ${PROFIL_COLORS[p]}30`, color: 'var(--admin-text)', outline: 'none' }} />
                </div>
              ))}
            </div>
            {form.type === 'vente' && (
              <div className="mt-2">
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Stock (vide = illimité)</label>
                <Input type="number" min="0" value={form.stock} onChange={e => setF('stock', e.target.value)}
                  placeholder="Ex: 50" style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)', maxWidth: 120 }} />
              </div>
            )}
          </div>

          {/* Tags + Options */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Tags (séparés par virgule)</label>
              <Input value={form.tags} onChange={e => setF('tags', e.target.value)}
                placeholder="mariage, anniversaire, jeux..."
                style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Poids d'affichage</label>
              <Input type="number" value={form.order_weight} onChange={e => setF('order_weight', parseInt(e.target.value)||0)}
                placeholder="50" style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
            </div>
          </div>

          {/* Produits à vendre associés */}
          {(form.type === 'prestation' || form.type === 'location') && (
            <div data-testid="associes-section">
              <label className="text-xs mb-2 block" style={{ color: 'var(--admin-muted)' }}>
                Produits à vendre associés <span style={{ color: 'var(--admin-faint)' }}>(proposés avec cette {form.type === 'location' ? 'location' : 'prestation'})</span>
              </label>
              {venteProducts.length === 0 ? (
                <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>Aucun produit à vendre disponible. Créez-en dans « Produits à vendre ».</p>
              ) : (
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 rounded-lg"
                  style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)' }}>
                  {venteProducts.map(vp => {
                    const sel = (form.produits_associes || []).includes(vp.id);
                    return (
                      <button key={vp.id} type="button" onClick={() => toggleAssocie(vp.id)}
                        data-testid={`associe-${vp.id}`}
                        className="text-xs px-2.5 py-1 rounded-full flex items-center gap-1.5 transition-colors"
                        style={{
                          background: sel ? 'rgba(31,157,122,0.18)' : 'rgba(255,255,255,0.04)',
                          color: sel ? '#1F9D7A' : 'var(--admin-muted)',
                          border: `1px solid ${sel ? 'rgba(31,157,122,0.4)' : 'var(--admin-border)'}`,
                        }}>
                        {sel && <Check size={11} />}{vp.nom}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Options */}
          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.disponible} onChange={e => setF('disponible', e.target.checked)} className="rounded" />
              <span className="text-sm" style={{ color: 'var(--admin-text)' }}>Visible sur le site</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={e => setF('featured', e.target.checked)} className="rounded" />
              <span className="text-sm" style={{ color: 'var(--admin-text)' }}>Bestseller</span>
            </label>
          </div>
        </div>

        <div className="flex gap-3 p-5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <Button onClick={submit} disabled={saving} className="flex-1"
            style={{ background: 'rgba(93,188,191,0.2)', color: 'var(--admin-teal)', border: '1px solid var(--admin-teal-line)' }}>
            {saving ? <RefreshCw size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
            {product ? 'Enregistrer les modifications' : 'Créer le produit'}
          </Button>
          <Button onClick={onCancel} variant="outline" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-muted)' }}>Annuler</Button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────
export default function AdminGestionProduits() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('prestation');
  const [catFilter, setCatFilter] = useState('');
  const [dispoFilter, setDispoFilter] = useState('all');  // all | visible | masque
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BACKEND}/api/products?limit=100`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (typeFilter) url += `&product_type=${typeFilter}`;
      if (catFilter) url += `&categorie_id=${catFilter}`;
      const [p, c] = await Promise.all([
        fetch(url).then(r => r.json()),
        fetch(`${BACKEND}/api/categories`).then(r => r.json()).catch(() => []),
      ]);
      let prods = p.products || [];
      if (dispoFilter === 'visible') prods = prods.filter(x => x.disponible);
      if (dispoFilter === 'masque') prods = prods.filter(x => !x.disponible);
      setProducts(prods);
      setTotal(p.total || prods.length);
      setCategories(Array.isArray(c) ? c : (c.categories || []));
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  }, [search, typeFilter, catFilter, dispoFilter]);

  useEffect(() => { load(); }, [load]);

  async function toggleDispo(product) {
    try {
      await fetch(`${BACKEND}/api/products/${product.id}`, {
        method: 'PUT', headers: H,
        body: JSON.stringify({ disponible: !product.disponible })
      });
      toast.success(!product.disponible ? 'Produit visible' : 'Produit masqué');
      load();
    } catch { toast.error('Erreur'); }
  }

  async function deleteProduct(product) {
    if (!window.confirm(`Supprimer "${product.nom}" ? Cette action est irréversible.`)) return;
    try {
      await fetch(`${BACKEND}/api/products/${product.id}`, { method: 'DELETE', headers: H });
      toast.success('Produit supprimé');
      load();
    } catch { toast.error('Erreur suppression'); }
  }

  async function moveToType(product, newType) {
    if (!newType || newType === product.type) return;
    try {
      await fetch(`${BACKEND}/api/products/${product.id}`, {
        method: 'PUT', headers: H, body: JSON.stringify({ type: newType }),
      });
      toast.success(`« ${product.nom} » déplacé vers ${TYPE_CONFIG[newType]?.label || newType}`);
      load();
    } catch { toast.error('Erreur lors du déplacement'); }
  }

  return (
    <div data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3" style={{ marginBottom: 18 }}>
        <div>
          <p className="adm-eyebrow">Produits</p>
          <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
            letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Gestion prestations</h2>
          <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>
            {total} élément(s) · prestations & services proposés (utilisez « Déplacer vers » pour reclasser)
          </p>
        </div>
        <Button onClick={() => { setEditProduct(null); setShowForm(true); }} size="sm"
          data-testid="new-prestation-btn"
          style={{ background: 'rgba(147,133,236,0.2)', color: '#9385EC', border: '1px solid rgba(147,133,236,0.4)' }}>
          <Plus size={14} className="mr-1" /> Nouvelle prestation
        </Button>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--admin-faint)' }} />
          <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9" style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
        </div>
        {[[
          typeFilter, setTypeFilter, [
            { value: '', label: 'Tous les univers' },
            { value: 'prestation', label: 'Prestations' },
            { value: 'location', label: 'Location' },
            { value: 'vente', label: 'Vente' },
            { value: 'precommande', label: 'Précommande' },
            { value: 'fabrication', label: 'Fabrication' },
          ]
        ], [
          dispoFilter, setDispoFilter, [
            { value: 'all', label: 'Tous statuts' },
            { value: 'visible', label: 'Visibles' },
            { value: 'masque', label: 'Masqués' },
          ]
        ]].map(([val, setter, opts], i) => (
          <select key={i} value={val} onChange={e => setter(e.target.value)}
            className="px-3 py-2 rounded-xl text-sm" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)' }}>
            {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ))}
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
          className="px-3 py-2 rounded-xl text-sm" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)' }}>
          <option value="">Toutes catégories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        <Button onClick={load} variant="outline" size="sm" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}>
          <RefreshCw size={14} />
        </Button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16 rounded-xl" style={{ background: 'var(--admin-surface)' }} />)}</div>
      ) : (
        <div className="space-y-2">
          {products.map(p => {
            const typeCfg = TYPE_CONFIG[p.type] || TYPE_CONFIG.location;
            return (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl" data-testid={`product-row-${p.id}`}
                style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)', opacity: p.disponible ? 1 : 0.55 }}>
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt={p.nom} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <Package size={18} style={{ color: 'var(--admin-faint)' }} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm truncate" style={{ color: 'var(--admin-text)' }}>{p.nom}</p>
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: typeCfg.bg, color: typeCfg.color }}>{typeCfg.label}</span>
                    {p.featured && <span className="text-xs" style={{ color: 'var(--admin-gold)' }}>★</span>}
                    {!p.disponible && <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(229,62,62,0.1)', color: 'var(--admin-red)' }}>Masqué</span>}
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--admin-faint)' }}>
                    {p.tarifs?.particulier > 0 ? `${p.tarifs.particulier}€` : 'Sur devis'}
                    {p.stock !== null && p.stock !== undefined ? ` · Stock: ${p.stock}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <select value="" onChange={e => moveToType(p, e.target.value)}
                    data-testid={`move-to-${p.id}`} title="Déplacer vers un autre univers"
                    className="text-xs px-2 py-1.5 rounded-lg"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--admin-border)', color: 'var(--admin-muted)' }}>
                    <option value="">Déplacer vers…</option>
                    {Object.entries(TYPE_CONFIG).filter(([k]) => k !== p.type).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                  <button onClick={() => toggleDispo(p)} title={p.disponible ? 'Masquer' : 'Afficher'}
                    className="p-2 rounded-lg transition-colors"
                    style={{ background: p.disponible ? 'rgba(31,157,122,0.1)' : 'rgba(255,255,255,0.05)', color: p.disponible ? '#1F9D7A' : 'rgba(255,255,255,0.3)' }}>
                    {p.disponible ? <Eye size={14}/> : <EyeOff size={14}/>}
                  </button>
                  <button onClick={() => { setEditProduct(p); setShowForm(true); }}
                    className="p-2 rounded-lg" style={{ background: 'rgba(125,177,255,0.08)', color: 'var(--admin-blue)' }}>
                    <Edit2 size={14}/>
                  </button>
                  <button onClick={() => deleteProduct(p)}
                    className="p-2 rounded-lg" style={{ background: 'var(--admin-red-dim)', color: 'var(--admin-red)' }}>
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            );
          })}
          {products.length === 0 && (
            <div className="text-center py-12" style={{ color: 'var(--admin-faint)' }}>
              <Package size={40} className="mx-auto mb-3 opacity-20" />
              <p>Aucun produit trouvé</p>
            </div>
          )}
        </div>
      )}

      {/* Modal formulaire */}
      {showForm && (
        <ProductForm
          product={editProduct}
          categories={categories}
          onSave={() => { setShowForm(false); setEditProduct(null); load(); }}
          onCancel={() => { setShowForm(false); setEditProduct(null); }}
        />
      )}
    </div>
  );
}
